import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { IAcademicLevel } from 'app/interfaces/academic-level.interface';
import { IClassroom } from 'app/interfaces/classroom.interface';
import { ILearner } from 'app/interfaces/learner.interface';
import { AcademicLevelsService } from 'app/services/AcademicLevels/academic-levels.service';
import { ClassroomService } from 'app/services/classroom/classroom.service';
import { LearnersService } from 'app/services/learners/learners.service';
import { isAdmin, isStaff } from 'app/utils/helpers.utils';
import { SeriesService } from 'app/services/series/series.service';
import { ISeries } from 'app/interfaces/series';
import { DepartmentsService } from 'app/services/departments/departments.service';
import { IDepartment } from 'app/interfaces/departments';
import { SpecializationService } from 'app/services/specialization/specialization.service';
import { ISpecialization } from 'app/interfaces/specialization';
import { FileHandler } from 'app/utils/file-handler.utils';


@Component({
  selector: 'app-student-registration',
  templateUrl: './student-registration.component.html',
  styleUrls: ['./student-registration.component.scss'],
  providers: [MessageService]
})
export class StudentRegistrationComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Form
  registrationForm: FormGroup;
  isSubmitting = false;
  currentStep = 1;
  totalSteps = 4;
  get stepsArray(): number[] {
    return Array.from({ length: this.totalSteps }, (_, i) => i + 1);
  }

  // Data
  academicLevels: IAcademicLevel[] = [];
  departments: IDepartment[] = [];
  series: ISeries[] = [];
  filteredSeries: ISeries[] = [];
  specializations: ISpecialization[] = [];
  filteredDepartments: IDepartment[] = [];
  // File upload
  profilePhoto: File | null = null;
  documents: File[] = [];
  maxFileSize = 5 * 1024 * 1024; // 5MB
  allowedImageTypes = ['image/jpeg', 'image/png', 'image/gif'];
  allowedDocumentTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

  // Options - will be loaded from translation service
  genderOptions: any[] = [];

  // Date validation
  maxDate = new Date();

  // Validation messages - will be loaded from translation service
  validationMessages: any = {};

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private learnersService: LearnersService,
    private seriesService: SeriesService,
    private departmentService: DepartmentsService,
    private academicLevelService: AcademicLevelsService,
    private specializationService: SpecializationService,
    private messageService: MessageService,
    private fileHandler: FileHandler,
    private translateService: TranslateService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadValidationMessages();
    this.loadRequiredData();
    this.setupFormSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load validation messages and options from translation service
   */
  private loadValidationMessages(): void {
    this.validationMessages = {
      required: this.translateService.instant('VALIDATION.REQUIRED'),
      email: this.translateService.instant('VALIDATION.EMAIL'),
      minlength: this.translateService.instant('VALIDATION.MIN_LENGTH'),
      maxlength: this.translateService.instant('VALIDATION.MAX_LENGTH'),
      pattern: this.translateService.instant('VALIDATION.PATTERN'),
      fileSize: this.translateService.instant('STUDENT_REGISTRATION.VALIDATION.FILE_SIZE'),
      fileType: this.translateService.instant('STUDENT_REGISTRATION.VALIDATION.FILE_TYPE'),
      phoneNumber: this.translateService.instant('VALIDATION.PHONE'),
      dateOfBirth: this.translateService.instant('STUDENT_REGISTRATION.VALIDATION.DATE_OF_BIRTH'),
      guardianPhone: this.translateService.instant('STUDENT_REGISTRATION.VALIDATION.GUARDIAN_PHONE')
    };

    this.genderOptions = [
      { label: this.translateService.instant('STUDENTS.MALE'), value: 'male' },
      { label: this.translateService.instant('STUDENTS.FEMALE'), value: 'female' },
      { label: this.translateService.instant('STUDENT_REGISTRATION.OTHER'), value: 'other' }
    ];
  }

  /**
   * Initializes the registration form
   */
  private initForm(): void {
    this.registrationForm = this.fb.group({
      // Personal Information
      personalInfo: this.fb.group({
        first_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
        last_name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
        email: ['', [Validators.required, Validators.email]],
        phone_number: ['', [Validators.required, Validators.pattern(/^\+?[\d\s\-\(\)]+$/)]],
        date_of_birth: ['', [Validators.required, this.dateOfBirthValidator.bind(this)]],
        gender: ['', Validators.required],
        address: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(200)]],
        place_of_birth: ['', [Validators.minLength(2), Validators.maxLength(100)]]
      }),

      // Family Information
      familyInfo: this.fb.group({
        fathers_name: ['', [Validators.minLength(2), Validators.maxLength(100)]],
        fathers_phone: ['', [Validators.pattern(/^\+?[\d\s\-\(\)]+$/)]],
        fathers_occupation: ['', [Validators.minLength(2), Validators.maxLength(100)]],
        mothers_name: ['', [Validators.minLength(2), Validators.maxLength(100)]],
        mothers_phone: ['', [Validators.pattern(/^\+?[\d\s\-\(\)]+$/)]],
        mothers_occupation: ['', [Validators.minLength(2), Validators.maxLength(100)]],
        guardian_name: ['', [Validators.minLength(2), Validators.maxLength(100)]],
        guardian_contact_phone: ['', [Validators.pattern(/^\+?[\d\s\-\(\)]+$/)]],
        guardian_address: ['', [Validators.minLength(10), Validators.maxLength(200)]],
        guardian_relationship: ['', [Validators.minLength(2), Validators.maxLength(50)]],
        guardian_occupation: ['', [Validators.minLength(2), Validators.maxLength(100)]]
      }),

      // Academic Information
      academicInfo: this.fb.group({
        academic_level: ['', Validators.required],
        specialization: ['', Validators.required],
        department: ['', Validators.required],
        series: ['', Validators.required],
        previous_school: ['', [Validators.minLength(2), Validators.maxLength(100)]],
        previous_grade: ['', [Validators.minLength(1), Validators.maxLength(10)]],
        transfer_reason: ['', [Validators.minLength(10), Validators.maxLength(500)]],
        special_needs: [''],
        medical_conditions: [''],
        emergency_contact: ['', [Validators.required, Validators.pattern(/^\+?[\d\s\-\(\)]+$/)]],
        emergency_relationship: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]]
      }),

      // Additional Information
      additionalInfo: this.fb.group({
        hobbies: ['', [Validators.minLength(5), Validators.maxLength(200)]],
        languages: ['', [Validators.minLength(2), Validators.maxLength(100)]],
        achievements: ['', [Validators.minLength(5), Validators.maxLength(500)]],
        goals: ['', [Validators.minLength(10), Validators.maxLength(500)]],
        notes: ['', [Validators.maxLength(1000)]]
      })
    });
  }

  /**
   * Sets up form subscriptions for dynamic validation
   */
  private setupFormSubscriptions(): void {
    // Filter classrooms when academic level changes
    this.registrationForm.get('academicInfo.department')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(departmentId => {
        this.filterSeriesByDepartment(departmentId);
      });

    this.registrationForm.get('academicInfo.specialization')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(specializationId => {
        this.filterDepartmentBySpecialization(specializationId);
      });
  }

  /**
   * Loads required data for the form
   */
  private loadRequiredData(): void {
    this.loadAcademicLevels();
    this.loadSeries();
    this.loadDepartments();
    this.loadSpecializations();
  }

  /**
   * Loads academic levels
   */
  private loadAcademicLevels(): void {
    this.academicLevelService.allAcademicLevels().subscribe({
      next: (response) => {
        this.academicLevels = response.data || [];
      },
      error: (error) => {
        console.error('Error loading academic levels:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('ERRORS.GENERAL'),
          detail: this.translateService.instant('ERRORS.GENERAL')
        });
      }
    });
  }


  /**
   * Loads departments
   */
  private loadDepartments(): void {
    this.departmentService.allDepartments().subscribe(response => {
      this.departments = response.data || [];
    });
  }

  /**
   * Loads series
   */
  private loadSeries(): void {
    this.seriesService.allSeries().subscribe(response => {
      this.series = response.data || [];
    });
  }

  /**
   * Loads specializations
   */
  private loadSpecializations(): void {
    this.specializationService.allSpecializations().subscribe(response => {
      this.specializations = response.data || [];
    });
  }

  /**
   * Filters classrooms by academic level
   */
  private filterSeriesByDepartment(departmentId: number): void {
    if (!departmentId) {
      this.filteredSeries = this.series;
      return;
    }

    this.filteredSeries = this.series.filter(series =>
      series.department?.id === departmentId
    );

    // Reset classroom selection if current selection is not valid
    const currentSeries = this.registrationForm.get('academicInfo.series')?.value;
    if (currentSeries && !this.filteredSeries.find(c => c.id === currentSeries)) {
      this.registrationForm.get('academicInfo.series')?.setValue('');
    }
  }

  /**
   * Filters departments by specialization
   */
  filterDepartmentBySpecialization(specializationId: number): void {
    if (!specializationId) {
      this.filteredDepartments = this.departments;
      return;
    }
    
    this.filteredDepartments = this.departments.filter(department =>
      department.specialization?.id === specializationId
    );

    // Reset department selection if current selection is not valid
    const currentDepartment = this.registrationForm.get('academicInfo.department')?.value;
    if (currentDepartment && !this.filteredDepartments.find(c => c.id === currentDepartment)) {
      this.registrationForm.get('academicInfo.department')?.setValue('');
    }
  }


  /**
   * Custom validator for date of birth
   */
  private dateOfBirthValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const birthDate = new Date(control.value);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    if (age < 5) {
      return { dateOfBirth: true };
    }

    return null;
  }

  /**
   * Handles profile photo selection
   */
  onProfilePhotoSelect(event: any): void {
    const file = event.files[0];
    if (this.validateFile(file, 'image')) {
      this.profilePhoto = file;
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('COMMON.SUCCESS'),
        detail: this.translateService.instant('STUDENT_REGISTRATION.MESSAGES.PROFILE_PHOTO_SUCCESS')
      });
    }
  }

  /**
   * Uploads profile photo using FileHandler
   */
  private uploadProfilePhoto(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.profilePhoto) {
        reject(new Error('No profile photo selected'));
        return;
      }

      const formData = new FormData();
      formData.append('file', this.profilePhoto);

      this.fileHandler.fileUpload(formData, 'profile_photo').subscribe({
        next: (response) => {
          resolve(response.data);
        },
        error: (error) => {
          console.error('Error uploading profile photo:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Handles document selection
   */
  onDocumentSelect(event: any): void {
    const files = event.files;
    const validFiles: File[] = [];
    
    for (const file of files) {
      if (this.validateFile(file, 'document')) {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      this.documents.push(...validFiles);
      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('COMMON.SUCCESS'),
        detail: this.translateService.instant('STUDENT_REGISTRATION.MESSAGES.DOCUMENTS_SUCCESS', { count: validFiles.length })
      });
    }
  }

  /**
   * Uploads documents using FileHandler
   */
  private uploadDocuments(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      if (this.documents.length === 0) {
        resolve([]);
        return;
      }

      const formData = new FormData();
      this.documents.forEach(file => {
        formData.append('files', file);
      });

      this.fileHandler.manyFilesUpload(formData, 'documents').subscribe({
        next: (response) => {
          if (response.data && Array.isArray(response.data)) {
            resolve(response.data);
          } else {
            resolve([]);
          }
        },
        error: (error) => {
          console.error('Error uploading documents:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Validates uploaded files
   */
  private validateFile(file: File, type: 'image' | 'document'): boolean {
    // Check file size
    if (file.size > this.maxFileSize) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('ERRORS.GENERAL'),
        detail: this.translateService.instant('STUDENT_REGISTRATION.VALIDATION.FILE_SIZE')
      });
      return false;
    }

    // Check file type
    if (type === 'image' && !this.allowedImageTypes.includes(file.type)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('ERRORS.GENERAL'),
        detail: this.translateService.instant('STUDENT_REGISTRATION.VALIDATION.FILE_TYPE')
      });
      return false;
    }

    if (type === 'document' && !this.allowedDocumentTypes.includes(file.type)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('ERRORS.GENERAL'),
        detail: this.translateService.instant('STUDENT_REGISTRATION.VALIDATION.FILE_TYPE')
      });
      return false;
    }

    return true;
  }

  /**
   * Removes a document from the list
   */
  removeDocument(index: number): void {
    this.documents.splice(index, 1);
  }

  /**
   * Removes profile photo
   */
  removeProfilePhoto(): void {
    this.profilePhoto = null;
  }

  /**
   * Navigates to the next step
   */
  nextStep(): void {
    if (this.validateCurrentStep()) {
      this.currentStep++;
    }
  }

  /**
   * Navigates to the previous step
   */
  previousStep(): void {
    this.currentStep--;
  }

  /**
   * Validates the current step
   */
  private validateCurrentStep(): boolean {
    let formGroup: FormGroup;

    switch (this.currentStep) {
      case 1:
        formGroup = this.registrationForm.get('personalInfo') as FormGroup;
        break;
      case 2:
        formGroup = this.registrationForm.get('familyInfo') as FormGroup;
        break;
      case 3:
        formGroup = this.registrationForm.get('academicInfo') as FormGroup;
        break;
      case 4:
        formGroup = this.registrationForm.get('additionalInfo') as FormGroup;
        break;
      default:
        return true;
    }

    if (formGroup.invalid) {
      this.markFormGroupTouched(formGroup);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('STUDENT_REGISTRATION.VALIDATION.VALIDATION_ERROR'),
        detail: this.translateService.instant('STUDENT_REGISTRATION.VALIDATION.FILL_REQUIRED_FIELDS')
      });
      return false;
    }

    return true;
  }

  /**
   * Marks all controls in a form group as touched
   */
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  /**
   * Submits the registration form
   */
  onSubmit(): void {
    if (this.registrationForm.invalid) {
      this.markFormGroupTouched(this.registrationForm);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('STUDENT_REGISTRATION.VALIDATION.VALIDATION_ERROR'),
        detail: this.translateService.instant('STUDENT_REGISTRATION.VALIDATION.FILL_REQUIRED_FIELDS')
      });
      return;
    }

    if (!this.profilePhoto) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('ERRORS.GENERAL'),
        detail: this.translateService.instant('STUDENT_REGISTRATION.VALIDATION.PROFILE_PHOTO_REQUIRED')
      });
      return;
    }

    this.isSubmitting = true;

    // First upload files, then submit registration
    this.uploadFilesAndSubmit();
  }

  /**
   * Uploads files first, then submits the registration
   */
  private async uploadFilesAndSubmit(): Promise<void> {
    try {
      // Upload profile photo
      this.messageService.add({
        severity: 'info',
        summary: this.translateService.instant('COMMON.LOADING'),
        detail: this.translateService.instant('STUDENT_REGISTRATION.MESSAGES.UPLOADING_PROFILE')
      });
      
      const profilePhotoUrl = await this.uploadProfilePhoto();
      
      // Upload documents
      this.messageService.add({
        severity: 'info',
        summary: this.translateService.instant('COMMON.LOADING'),
        detail: this.translateService.instant('STUDENT_REGISTRATION.MESSAGES.UPLOADING_DOCUMENTS')
      });
      
      const documentUrls = await this.uploadDocuments();
      
      // Prepare JSON payload with uploaded file URLs
      const registrationPayload = this.prepareRegistrationPayload(profilePhotoUrl, documentUrls);

      // Submit registration
      this.learnersService.addLearner(registrationPayload).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('COMMON.SUCCESS'),
            detail: this.translateService.instant('STUDENT_REGISTRATION.MESSAGES.REGISTRATION_SUCCESS')
          });

          // Navigate to the new student's profile
          setTimeout(() => {
            this.router.navigate(['/app/learners/profile', response.data.id]);
          }, 2000);
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('Error registering student:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('ERRORS.GENERAL'),
            detail: error.error?.message || this.translateService.instant('STUDENT_REGISTRATION.MESSAGES.REGISTRATION_ERROR')
          });
        }
      });
    } catch (error) {
      this.isSubmitting = false;
      console.error('Error uploading files:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('ERRORS.GENERAL'),
        detail: this.translateService.instant('STUDENT_REGISTRATION.MESSAGES.UPLOAD_ERROR')
      });
    }
  }

  /**
   * Prepares JSON payload for registration submission
   */
  private prepareRegistrationPayload(profilePhotoUrl: string, documentUrls: string[]): any {
    const formValue = this.registrationForm.value;
    
    // Flatten the form structure
    const payload: any = {};
    
    // Add personal information
    Object.assign(payload, formValue.personalInfo);
    
    // Add family information
    Object.assign(payload, formValue.familyInfo);
    
    // Add academic information
    Object.assign(payload, formValue.academicInfo);
    
    // Add additional information
    Object.assign(payload, formValue.additionalInfo);
    
    // Add uploaded file URLs
    payload.profile_photo = profilePhotoUrl;
    payload.documents = documentUrls;
    
    // Remove empty values
    Object.keys(payload).forEach(key => {
      if (payload[key] === null || payload[key] === undefined || payload[key] === '') {
        delete payload[key];
      }
    });

    return payload;
  }

  /**
   * Gets validation message for a field
   */
  getValidationMessage(controlName: string, section: string): string {
    const control = this.registrationForm.get(`${section}.${controlName}`);
    if (!control || !control.errors || !control.touched) return '';

    const error = Object.keys(control.errors)[0];
    let message = this.validationMessages[error] || 'Invalid input';

    if (error === 'minlength') {
      message = message.replace('{0}', control.errors[error].requiredLength);
    } else if (error === 'maxlength') {
      message = message.replace('{0}', control.errors[error].requiredLength);
    }

    return message;
  }

  /**
   * Gets photo preview URL for display
   */
  getPhotoPreview(): string {
    if (!this.profilePhoto) return '';
    return URL.createObjectURL(this.profilePhoto);
  }

  /**
   * Checks if a field is invalid
   */
  isFieldInvalid(controlName: string, section: string): boolean {
    const control = this.registrationForm.get(`${section}.${controlName}`);
    return !!(control && control.invalid && control.touched);
  }

  /**
   * Gets step title
   */
  getStepTitle(step: number): string {
    const titles = [
      this.translateService.instant('STUDENT_REGISTRATION.STEPS.PERSONAL_INFO'),
      this.translateService.instant('STUDENT_REGISTRATION.STEPS.FAMILY_INFO'),
      this.translateService.instant('STUDENT_REGISTRATION.STEPS.ACADEMIC_INFO'),
      this.translateService.instant('STUDENT_REGISTRATION.STEPS.ADDITIONAL_INFO')
    ];
    return titles[step - 1] || '';
  }

  /**
   * Gets step description
   */
  getStepDescription(step: number): string {
    const descriptions = [
      this.translateService.instant('STUDENT_REGISTRATION.STEP_DESCRIPTIONS.PERSONAL_INFO'),
      this.translateService.instant('STUDENT_REGISTRATION.STEP_DESCRIPTIONS.FAMILY_INFO'),
      this.translateService.instant('STUDENT_REGISTRATION.STEP_DESCRIPTIONS.ACADEMIC_INFO'),
      this.translateService.instant('STUDENT_REGISTRATION.STEP_DESCRIPTIONS.ADDITIONAL_INFO')
    ];
    return descriptions[step - 1] || '';
  }

  /**
   * Checks if user has permission to register students
   */
  get canRegisterStudents(): boolean {
    return isAdmin() || isStaff();
  }

  /**
   * Gets academic level options for dropdown
   */
  getAcademicLevelOptions(): any[] {
    return this.academicLevels.map(level => ({
      label: level.name,
      value: level.id
    }));
  }
}
